import React from "react";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useDailyReward } from "@/lib/useDailyReward";
import { dailyRewardLabel, dailyRewardTerms, dailyRewardClaimMessage } from "@/lib/dailyReward";

/* DailyRewardCard — the ONE daily-reward claim container, rendered identically on
   mobile Home and the mobile Casino lobby (same component → aligned at all times).
   Styling intentionally mirrors the Home "Top up & play" feature card: a tall card,
   the #19191D→#0F0F12 gradient, a green top-right radial glow, and a large, subtle
   PCoin watermark in the background — not a flat row. Both states are driven by the
   real /wallet.bonus (can_claim + next_claim_at):
     • claimable → green "Claim" button (POST /wallet/claim-bonus, then refresh)
     • claimed   → a LIVE ticking countdown to next_claim_at REPLACES the button
   When the countdown elapses it refetches so the Claim button returns on its own.
   No money-path code here — it calls the same tested /wallet endpoints Home/Rewards
   already use; the server's can_claim gate is the source of truth. */
export default function DailyRewardCard({ style }) {
  const { user } = useAuth();
  const {
    bonus, loading: bonusLoading, error: bonusError, claiming,
    claim: claimReward, reload: loadBonus, countdown, canClaim, onCooldown,
  } = useDailyReward();
  const streak = user?.vip?.daily_streak;

  const claim = async () => {
    try {
      const data = await claimReward();
      if (data) toast.success(dailyRewardClaimMessage(data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Come back later for your next reward.");
    }
  };

  return (
    <div
      style={{
        position: "relative", margin: "0 16px", minHeight: 136, boxSizing: "border-box",
        borderRadius: 20, overflow: "hidden",
        background: "linear-gradient(180deg,#19191D 0%,#0F0F12 100%)",
        boxShadow: "var(--m-elev-door)",
        ...style,
      }}
    >
      {/* green top-right radial glow — same signature as the "Top up & play" card */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(90% 120% at 100% 0%,rgba(0,214,50,.16) 0%,rgba(0,214,50,0) 58%)" }} />
      {/* subtle PCoin watermark sunk into the background */}
      <Icon name="pnrcoin" size={150} style={{ position: "absolute", right: -28, bottom: -44, color: "#00D632", opacity: 0.08, pointerEvents: "none" }} />

      <div style={{ position: "relative", padding: 16, minHeight: 136, display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ font: "800 10px 'Outfit'", letterSpacing: ".18em", textTransform: "uppercase", color: "var(--m-green)" }}>Daily reward</span>
          <span style={{ font: "800 22px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>{onCooldown ? "Daily reward claimed" : "Free daily reward"}</span>
          <span style={{ font: "500 12px 'Outfit'", color: "var(--m-muted)" }}>
            {bonusError ? "Unable to load your reward. Please retry." : bonus
              ? <>{canClaim ? "Claim " : "Next reward: "}<b className="m-tnum" style={{ color: "#fff", fontWeight: 700 }}>{dailyRewardLabel(bonus)}</b> every 24h</>
              : "Loading your daily reward…"}
            {streak ? <> · day {streak} streak</> : null}
          </span>
          {bonus && <span style={{ font: "500 11px 'Outfit'", color: "var(--m-muted)" }}>{dailyRewardTerms(bonus)}</span>}
        </div>

        {!onCooldown ? (
          <button
            data-testid="mobile-daily-claim" type="button" onClick={bonusError ? loadBonus : claim} disabled={claiming || bonusLoading || (!canClaim && !bonusError)}
            style={{ alignSelf: "flex-start", height: 42, padding: "0 20px", borderRadius: 12, border: 0, cursor: claiming ? "default" : "pointer",
              font: "800 14px 'Manrope'", display: "inline-flex", alignItems: "center", gap: 7,
              background: "var(--m-green)", color: "var(--m-on-green)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,.3), 0 6px 16px rgba(0,214,50,.28)", opacity: claiming || bonusLoading ? 0.6 : 1 }}
          >
            <Icon name="pnrcoin" size={15} style={{ color: "var(--m-on-green)" }} /> {bonusError ? "Retry reward" : bonusLoading ? "Loading…" : claiming ? "Claiming…" : "Claim"}
          </button>
        ) : (
          // Live countdown REPLACES the claim button once the reward is claimed.
          <span
            aria-label={`Next reward in ${countdown}`}
            style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 8, height: 42, padding: "0 16px", borderRadius: 12,
              background: "rgba(10,10,10,.55)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08)" }}
          >
            <Icon name="clock" size={16} style={{ color: "var(--m-green)" }} />
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
              <span style={{ font: "700 8px 'Outfit'", letterSpacing: ".14em", color: "#71717a", textTransform: "uppercase" }}>Next drop in</span>
              <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: "#fff" }}>{countdown || "soon"}</span>
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

